/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * The Audit pages, rebuilt on the kit (overhaul plan step 3g): Audit › Activity (the trace explorer), Audit › Audit log
 * (the tamper-evident ledger, its capacity and the lifecycle faults) and Audit › Approvals (the console's one inbox for
 * pending actions). The same calls and the same decisions as before; a turn or an approval opens in a detail row under
 * its own, money reads in the console's one format, and status is a chip with a word.
 */
import { useEffect, useState } from 'react';
import { PetLibraryReviews } from '../components/PetLibraryReviews';
import { Activity as ActivityIcon, Download, Eye, Inbox, RefreshCw, ScrollText, ShieldAlert, ShieldCheck, ThumbsDown, ThumbsUp } from 'lucide-react';
import {
  ApiError,
  api,
  type AdminApproval,
  type AuditCapacity,
  type AuditRow,
  type LifecycleFaultSummary,
  type DesktopReconciliationDisposition,
  type TraceRow,
} from '../lib/api';
import { auditCapacityView } from '../lib/auditCapacity';
import { auditExportHref } from '../lib/governance';
import {
  Button, Callout, Checkbox, EmptyState, Field, Icon, Input, LinkButton, Meter, PageHeader, Panel, RISK_CHIPS, Select, SpecChip, Stack, StatusChip, Table, Tag,
  type Column, type Tone,
} from '../ui';
import { count, dateTime, money } from '../ui/format';

/** Which view the page shows: Audit › Activity (traces), Audit › Audit log, or Audit › Approvals, the one inbox. */
export type ActivityView = 'traces' | 'audit' | 'approvals';

const fmtTime = (s: string | number) => dateTime(s);
const localDateTimeValue = (value = Date.now()) => {
  const date = new Date(value);
  return new Date(value - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

interface ReconciliationContext {
  settleAfter?: number;
  source?: 'client-reported-uncertain' | 'lease-expired';
  expectedStateVersion: number;
  expectedRecordProof: string;
}

interface ReconciliationEvidence {
  disposition: DesktopReconciliationDisposition;
  evidenceReference: string;
  evidenceSha256: string;
  evidenceObservedAt: string;
  acknowledgeNoReplay: boolean;
}

const VIEW_SUB: Record<ActivityView, string> = {
  traces: 'Every Gyro turn, with its tools, time and cost: the trace explorer.',
  audit: 'Every action and admin change, in the tamper-evident audit ledger.',
  approvals: "Actions waiting for an admin to approve or reject: the console's one inbox for pending actions.",
};

/** A risk tier as a chip from the kit's map, or the service's own word when it is not one of the five. */
function RiskChip({ tier }: { tier?: string }) {
  const spec = tier && tier in RISK_CHIPS ? RISK_CHIPS[tier as keyof typeof RISK_CHIPS] : null;
  return spec ? <SpecChip spec={spec} /> : <StatusChip tone="neutral">{tier ?? 'standard'}</StatusChip>;
}

/** A JSON value as the record holds it. */
const Json = ({ value }: { value: unknown }) => <pre className="ui-pre">{JSON.stringify(value, null, 2)}</pre>;

/** The Audit pages (2026-10-11): each view is its own page in the rail, so the screen no longer draws tab buttons. */
export function Activity({ view = 'traces', petsAvailable = false, canModeratePets = false }: { view?: ActivityView; petsAvailable?: boolean; canModeratePets?: boolean } = {}) {
  const tab = view;
  const [traces, setTraces] = useState<TraceRow[]>([]);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [chain, setChain] = useState<{ ok: boolean; brokenAtSeq?: number } | null>(null);
  const [capacity, setCapacity] = useState<AuditCapacity | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleFaultSummary | null>(null);
  const [approvals, setApprovals] = useState<Awaited<ReturnType<typeof api.approvals>>['approvals']>([]);
  const [legacyQuarantine, setLegacyQuarantine] = useState<Awaited<ReturnType<typeof api.approvals>>['legacyQuarantine']>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [expandedApproval, setExpandedApproval] = useState<string | null>(null);
  const [reviewedApprovals, setReviewedApprovals] = useState<Record<string, boolean>>({});
  const [approvalBusy, setApprovalBusy] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState('');
  const [reconciliationContexts, setReconciliationContexts] = useState<Record<string, ReconciliationContext>>({});
  const [reconciliationEvidence, setReconciliationEvidence] = useState<Record<string, ReconciliationEvidence>>({});
  const [nowMs, setNowMs] = useState(() => Date.now());

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'traces') setTraces((await api.traces({ errors: errorsOnly, limit: 200 })).traces);
      else if (tab === 'audit') {
        const a = await api.audit({ limit: 200 });
        setAuditRows(a.audit);
        setChain(a.chain);
        // Absent on a service older than the admin build: render nothing rather than a false "ok".
        setCapacity(a.capacity ?? null);
        setLifecycle(a.lifecycle ?? null);
      } else {
        const result = await api.approvals();
        setApprovals(result.approvals);
        setLegacyQuarantine(result.legacyQuarantine);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, errorsOnly]);

  useEffect(() => {
    if (tab !== 'approvals') return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [tab]);

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    const item = approvals.find((approval) => approval.id === id);
    if (!item) return;
    setApprovalBusy(id);
    setApprovalError('');
    try {
      await api.decideApproval(id, decision, item.governedPreview);
      await load();
    } catch (error) {
      setApprovalError(error instanceof Error ? error.message : 'The approval decision failed.');
      // The request may have changed under the decision (ended because the requester blocked its tool, decided by
      // someone else): show the queue as it stands now. The sentence above stays; a failed reload changes nothing.
      await load().catch(() => undefined);
    } finally {
      setApprovalBusy(null);
    }
  };

  const reconcile = async (id: string, submitDisposition = false) => {
    const item = approvals.find((approval) => approval.id === id);
    if (!item) return;
    setApprovalBusy(id);
    setApprovalError('');
    try {
      const evidence = reconciliationEvidence[id];
      const context = reconciliationContexts[id];
      const desktopRequest = submitDisposition && item.app === 'desktop' && evidence?.acknowledgeNoReplay && context ? {
        disposition: evidence.disposition,
        evidenceReference: evidence.evidenceReference,
        evidenceSha256: evidence.evidenceSha256,
        evidenceObservedAt: Date.parse(evidence.evidenceObservedAt),
        acknowledgeNoReplay: true as const,
        expectedStateVersion: context.expectedStateVersion,
        expectedRecordProof: context.expectedRecordProof,
      } : undefined;
      const providerRequest = submitDisposition && item.app !== 'desktop' && evidence?.acknowledgeNoReplay ? {
        disposition: 'operator-confirmed-not-applied' as const,
        evidenceReference: evidence.evidenceReference,
        evidenceSha256: evidence.evidenceSha256,
        acknowledgeNoReplay: true as const,
      } : undefined;
      const result = await api.reconcileApproval(id, desktopRequest ?? providerRequest);
      if (result.dispositionRequired) {
        setReconciliationContexts((current) => ({
          ...current,
          [id]: {
            settleAfter: result.settleAfter,
            source: result.source,
            expectedStateVersion: result.expectedStateVersion ?? item.stateVersion,
            expectedRecordProof: result.expectedRecordProof ?? item.recordProof,
          },
        }));
        setReconciliationEvidence((current) => current[id] ? current : {
          ...current,
          [id]: {
            disposition: 'operator-confirmed-not-applied',
            evidenceReference: '',
            evidenceSha256: '',
            evidenceObservedAt: localDateTimeValue(),
            acknowledgeNoReplay: false,
          },
        });
      } else {
        setReconciliationContexts((current) => { const next = { ...current }; delete next[id]; return next; });
        setReconciliationEvidence((current) => { const next = { ...current }; delete next[id]; return next; });
        await load();
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 409
        && (error.body?.error === 'action_state_changed' || error.body?.error === 'desktop_disposition_conflict')) {
        setReconciliationContexts((current) => { const next = { ...current }; delete next[id]; return next; });
        setReconciliationEvidence((current) => { const next = { ...current }; delete next[id]; return next; });
        await load();
        setApprovalError('The action changed after review. The refreshed record must be reviewed before another disposition.');
      } else {
        setApprovalError(error instanceof Error ? error.message : 'Reconciliation failed.');
      }
    } finally {
      setApprovalBusy(null);
    }
  };

  // ---- Traces ----

  const traceColumns: Array<Column<TraceRow>> = [
    { key: 'time', header: 'Time', muted: true, cell: (t) => fmtTime(t.ts) },
    { key: 'app', header: 'App', cell: (t) => <Tag>{t.app}</Tag> },
    // The API the model ran on, under the id, when it is not Anthropic (OpenAI models in the picker, 2026-10-22).
    { key: 'model', header: 'Model', muted: true, mono: true, hideOnPhone: true, cell: (t) => <>{t.model}{t.provider === 'openai' ? <span className="ui-cell-sub"> openai</span> : null}</> },
    { key: 'rounds', header: 'Rounds', numeric: true, hideOnPhone: true, cell: (t) => count(t.rounds) },
    { key: 'ms', header: 'ms', numeric: true, hideOnPhone: true, cell: (t) => count(t.durationMs) },
    { key: 'cost', header: 'Cost', numeric: true, cell: (t) => money(t.costUsd) },
    { key: 'tools', header: 'Tools', numeric: true, cell: (t) => <>{count(t.tools.length)}{t.error ? <span className="ui-text-bad"> · err</span> : ''}</> },
    {
      key: 'rating', header: 'Rating', align: 'center', width: '72px',
      cell: (t) => (t.rating === 'up' ? <Icon icon={ThumbsUp} size="sm" label="Rated up" className="ui-text-ok" /> : t.rating === 'down' ? <Icon icon={ThumbsDown} size="sm" label="Rated down" className="ui-text-bad" /> : null),
    },
  ];

  const toolColumns: Array<Column<TraceRow['tools'][number] & { index: number }>> = [
    { key: 'tool', header: 'Tool', cell: (x) => <Tag>{x.name}</Tag> },
    { key: 'decision', header: 'Decision', cell: (x) => x.decision },
    { key: 'risk', header: 'Risk', cell: (x) => (x.riskTier ? <RiskChip tier={x.riskTier} /> : '—') },
    { key: 'ms', header: 'ms', numeric: true, cell: (x) => count(x.ms) },
    { key: 'ok', header: 'ok', align: 'center', cell: (x) => (x.ok ? <StatusChip tone="ok">ok</StatusChip> : <StatusChip tone="bad">failed</StatusChip>) },
  ];

  const traceDetail = (t: TraceRow) => expanded === t.traceId && (
    <div className="ui-form ui-detail">
      {/* Question text is never stored (privacy policy: the usage log holds a hashed id, not the conversation). */}
      <p className="ui-cell-sub">{typeof t.questionChars === 'number' ? `question: ${t.questionChars} characters` : 'question text is not kept'} · user <code>{t.userHash}</code></p>
      <p className="ui-para">trace <code>{t.traceId}</code> · trust {t.trust} · {count(t.inputTokens)}+{count(t.outputTokens)} tok{t.cacheReadTokens || t.cacheWriteTokens ? ` · cache ${count(t.cacheReadTokens ?? 0)} read, ${count(t.cacheWriteTokens ?? 0)} written` : ''} · stop {t.stopReason ?? '—'}</p>
      {/* What went wrong, as the trace keeps it (a failed model call's kind, status, type, request id and the API's message, redacted): the person was only told a plain sentence. */}
      {t.error && <p className="ui-para"><span className="ui-text-bad">error</span> <code className="ui-break">{t.error}</code></p>}
      {t.tools.length > 0 && (
        <div className="ui-table-box">
          <Table stacked label="Tools" columns={toolColumns} rows={t.tools.map((x, index) => ({ ...x, index }))} rowKey={(x) => String(x.index)} stickyHeader={false} />
        </div>
      )}
      {t.skills.length > 0 && <p className="ui-cell-sub">skills: {t.skills.map((s) => `${s.id}(${s.score})`).join(', ')}</p>}
    </div>
  );

  // ---- Audit log ----

  const auditColumns: Array<Column<AuditRow>> = [
    { key: 'seq', header: '#', numeric: true, muted: true, width: '64px', cell: (a) => a.seq },
    { key: 'time', header: 'Time', muted: true, cell: (a) => fmtTime(a.ts) },
    { key: 'actor', header: 'Actor', muted: true, cell: (a) => a.actor.email ?? a.actor.id },
    { key: 'event', header: 'Event', cell: (a) => <Tag>{a.event}</Tag> },
    { key: 'app', header: 'App', hideOnPhone: true, cell: (a) => a.app ?? '—' },
    { key: 'tool', header: 'Tool', muted: true, mono: true, hideOnPhone: true, cell: (a) => a.tool ?? '—' },
    { key: 'outcome', header: 'Outcome', muted: true, cell: (a) => a.outcome ?? a.detail ?? '—' },
  ];

  const faultColumns: Array<Column<LifecycleFaultSummary['faults'][number]>> = [
    { key: 'last', header: 'Last failed', muted: true, cell: (f) => fmtTime(f.lastFailedAt) },
    { key: 'app', header: 'App', cell: (f) => <Tag>{f.app}</Tag> },
    { key: 'event', header: 'Event', muted: true, cell: (f) => f.kind },
    { key: 'action', header: 'Action', muted: true, mono: true, cell: (f) => f.actionId },
    { key: 'attempts', header: 'Attempts', numeric: true, cell: (f) => count(f.attempts) },
    {
      key: 'state', header: 'State',
      cell: (f) => (
        <span title={f.nextAttemptAt ? `next retry ${fmtTime(f.nextAttemptAt)}` : undefined}>
          {f.quarantinedAt ? <StatusChip tone="bad">set aside</StatusChip> : <StatusChip tone="warn">retrying{f.lastClass === 'deterministic' ? ' (repeats)' : ''}</StatusChip>}
        </span>
      ),
    },
    { key: 'error', header: 'Error', muted: true, cell: (f) => <span className="ui-break">{f.lastError}</span> },
  ];

  const capacityView = capacity ? auditCapacityView(capacity) : null;

  // ---- Approvals ----

  const approvalColumns: Array<Column<AdminApproval>> = [
    { key: 'time', header: 'Time', muted: true, cell: (a) => fmtTime(a.at) },
    { key: 'app', header: 'App', cell: (a) => <Tag>{a.app}</Tag> },
    { key: 'tool', header: 'Tool', muted: true, mono: true, cell: (a) => a.tool },
    { key: 'summary', header: 'Summary', width: '26%', cell: (a) => a.summary },
    { key: 'risk', header: 'Risk', cell: (a) => <RiskChip tier={a.riskTier} /> },
    { key: 'by', header: 'Requested by', muted: true, hideOnPhone: true, cell: (a) => a.requestedBy },
    {
      key: 'status', header: 'Status',
      cell: (a) => (a.status === 'awaiting-approval' ? <StatusChip tone="warn">awaiting approval</StatusChip>
        : a.status === 'reconciliation-required' ? <StatusChip tone="warn">reconciliation required</StatusChip>
          : <StatusChip tone="neutral">{a.status}</StatusChip>),
    },
    {
      key: 'review', header: <span className="ui-sr">Review</span>, align: 'end',
      cell: (a) => <Button size="sm" icon={Eye} aria-expanded={expandedApproval === a.id} onClick={() => setExpandedApproval(expandedApproval === a.id ? null : a.id)}>Review</Button>,
    },
  ];

  const approvalDetail = (a: AdminApproval) => {
    if (expandedApproval !== a.id) return null;
    const expired = Boolean(a.governedPreview && a.governedPreview.expiresAt * 1000 <= nowMs);
    const reviewKey = `${a.id}:${a.governedPreview?.previewId ?? 'ordinary'}:${a.governedPreview?.implementationRevision ?? ''}`;
    return (
      <div className="ui-form ui-detail">
        <Field label="Exact input"><Json value={a.input} /></Field>
        {a.governedPreview && (
          <>
            <div className="ui-actions">
              <span>Preview receipt <code>{a.governedPreview.previewId}</code></span>
              <span>Release <code>{a.governedPreview.implementationRevision}</code></span>
              <StatusChip tone={expired ? 'warn' : 'ok'}>{expired ? 'preview expired' : `expires ${fmtTime(a.governedPreview.expiresAt * 1000)}`}</StatusChip>
            </div>
            <Field label="Provider impact preview"><Json value={a.governedPreview.preview} /></Field>
          </>
        )}
        {a.run && <Field label="Durable provider run"><Json value={a.run} /></Field>}
        {a.errorMessage && <Callout tone="bad">{a.errorMessage}</Callout>}
        {a.status === 'awaiting-approval' && (
          <>
            <Checkbox checked={Boolean(reviewedApprovals[reviewKey])} onChange={(event) => setReviewedApprovals((current) => ({ ...current, [reviewKey]: event.target.checked }))} label="I reviewed the exact input and provider impact preview." />
            <div className="ui-actions">
              <Button variant="primary" busy={approvalBusy === a.id} disabled={!reviewedApprovals[reviewKey] || expired} onClick={() => void decide(a.id, 'approve')}>Approve</Button>
              <Button disabled={approvalBusy === a.id} onClick={() => void decide(a.id, 'reject')}>Reject</Button>
            </div>
          </>
        )}
        {a.operatorDisposition && <Field label="Operator disposition"><Json value={a.operatorDisposition} /></Field>}
        {a.lateClientReceipt && (
          <Field label={<>Late device receipt {a.lateClientReceipt.conflictsWithDisposition && <StatusChip tone="warn">conflicts with disposition</StatusChip>}</>}>
            <Json value={a.lateClientReceipt} />
          </Field>
        )}
        {a.status === 'reconciliation-required' && (
          <div className="ui-form">
            <div className="ui-actions">
              <Button variant="primary" icon={RefreshCw} busy={approvalBusy === a.id} onClick={() => void reconcile(a.id)}>{a.app === 'desktop' ? 'Review disposition' : 'Check provider run'}</Button>
            </div>
            {reconciliationContexts[a.id] && (() => {
              const context = reconciliationContexts[a.id]!;
              const evidence = reconciliationEvidence[a.id] ?? {
                disposition: 'operator-confirmed-not-applied' as const,
                evidenceReference: '',
                evidenceSha256: '',
                evidenceObservedAt: localDateTimeValue(),
                acknowledgeNoReplay: false,
              };
              const observedAt = Date.parse(evidence.evidenceObservedAt);
              const valid = evidence.evidenceReference.trim().length >= 8
                && /^[a-f0-9]{64}$/.test(evidence.evidenceSha256)
                && (a.app !== 'desktop' || Number.isFinite(observedAt));
              const updateEvidence = (patch: Partial<typeof evidence>) => setReconciliationEvidence((current) => ({ ...current, [a.id]: { ...evidence, ...patch } }));
              const available = !context.settleAfter || nowMs >= context.settleAfter;
              const actionLabel = evidence.disposition === 'operator-confirmed-applied'
                ? 'Confirm applied'
                : evidence.disposition === 'operator-confirmed-not-applied'
                  ? 'Confirm not applied'
                  : 'Confirm partial or wrong state';
              return (
                <div className="ui-form">
                  <Callout tone="warn">
                    {context.source === 'client-reported-uncertain'
                      ? 'The device reported an uncertain outcome. Independent evidence is required.'
                      : context.settleAfter
                        ? `No authoritative completion was received; disposition available ${fmtTime(context.settleAfter)}`
                        : 'No provider run was found. Independent evidence is required.'}
                  </Callout>
                  {a.app === 'desktop' && (
                    <Field label="Disposition">
                      <Select aria-label="Desktop disposition" value={evidence.disposition} onChange={(event) => updateEvidence({ disposition: event.target.value as DesktopReconciliationDisposition })}>
                        <option value="operator-confirmed-applied">Applied as intended</option>
                        <option value="operator-confirmed-not-applied">Not applied</option>
                        <option value="operator-confirmed-partial-or-wrong-state">Partial or wrong state</option>
                      </Select>
                    </Field>
                  )}
                  <Field label="Evidence reference">
                    <Input aria-label="Evidence reference" placeholder="Evidence reference" value={evidence.evidenceReference} onChange={(event) => updateEvidence({ evidenceReference: event.target.value })} />
                  </Field>
                  <Field label="Evidence SHA-256">
                    <Input className="is-mono" aria-label="Evidence SHA-256" placeholder="Evidence SHA-256" value={evidence.evidenceSha256} onChange={(event) => updateEvidence({ evidenceSha256: event.target.value.trim().toLowerCase() })} />
                  </Field>
                  {a.app === 'desktop' && (
                    <Field label="Evidence observed at">
                      <Input aria-label="Evidence observed at" type="datetime-local" value={evidence.evidenceObservedAt} onChange={(event) => updateEvidence({ evidenceObservedAt: event.target.value })} />
                    </Field>
                  )}
                  <Checkbox checked={evidence.acknowledgeNoReplay} onChange={(event) => updateEvidence({ acknowledgeNoReplay: event.target.checked })} label="I verified the independent evidence and understand this action will not be replayed." />
                  <p className="ui-cell-sub">Reviewed state version {context.expectedStateVersion}; the service will reject this disposition if the sealed action changes.</p>
                  <div className="ui-actions">
                    <Button variant="primary" icon={ShieldCheck} busy={approvalBusy === a.id} disabled={!available || !valid || !evidence.acknowledgeNoReplay} onClick={() => void reconcile(a.id, true)}>{a.app === 'desktop' ? actionLabel : 'Confirm not applied'}</Button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const quarantineColumns: Array<Column<(typeof legacyQuarantine)[number]>> = [
    { key: 'when', header: 'Quarantined', muted: true, cell: (e) => fmtTime(e.quarantinedAt * 1000) },
    { key: 'app', header: 'App', cell: (e) => <Tag>{e.app}</Tag> },
    { key: 'action', header: 'Action', muted: true, mono: true, cell: (e) => e.actionId },
    { key: 'summary', header: 'Summary', cell: (e) => e.summary || 'Unavailable' },
    { key: 'owner', header: 'Owner', muted: true, cell: (e) => e.userId },
    { key: 'prior', header: 'Prior state', cell: (e) => <StatusChip tone="warn">{e.priorStatus}</StatusChip> },
    { key: 'evidence', header: 'Evidence', cell: (e) => <Tag>{e.recordSha256.slice(0, 12)}</Tag> },
  ];

  const chainTone: Tone = chain ? (chain.ok ? 'ok' : 'warn') : 'neutral';

  return (
    <>
      <PageHeader
        meta={<span>{VIEW_SUB[tab]}</span>}
        actions={<Button icon={RefreshCw} onClick={() => void load()} busy={loading} aria-label="Refresh" className="is-collapse-phone">Refresh</Button>}
      />
      <div className="ui-page">
        <Stack>
          {tab === 'traces' && (
            <Panel
              flush
              title={<>Recent turns ({traces.length})</>}
              description="The last 200 turns, newest first; open a row for its tools and what the trace kept"
              actions={<Checkbox label="errors only" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />}
            >
              <Table stacked
                label="Recent turns"
                columns={traceColumns}
                rows={traces}
                rowKey={(t) => t.traceId}
                loading={loading}
                skeletonRows={6}
                selectedKey={expanded}
                onRowClick={(t) => setExpanded(expanded === t.traceId ? null : t.traceId)}
                detail={traceDetail}
                empty={<EmptyState compact icon={ActivityIcon} title="No traces yet">They appear once apps start asking Gyro.</EmptyState>}
              />
            </Panel>
          )}

          {tab === 'audit' && (
            <>
              {capacity && capacityView && capacityView.alert && (
                <Callout tone={capacityView.tone === 'bad' ? 'bad' : 'warn'} role="alert" icon={ShieldAlert} title={capacity.level === 'exhausted' ? 'The audit receipt store is full' : `The audit receipt store is ${capacityView.percent}% full`}>
                  {capacity.level === 'exhausted'
                    ? 'Until receipt retention is changed, actions that need confirmation or approval are refused, chat turns that propose an action fail, and once a device change is waiting to be recorded every desktop PC call fails too. Only plain chat and reads keep working.'
                    : `${count(capacity.receiptsRemaining)} left. When it is full, actions that need confirmation or approval are refused, chat turns that propose an action fail, and desktop PCs stop working once a device change is waiting to be recorded. ${capacity.level === 'critical' ? 'Treat this as an outage about to happen: change receipt retention now.' : 'Plan the receipt retention change now.'}`}
                </Callout>
              )}
              {lifecycle && (lifecycle.stuck > 0 || lifecycle.quarantined > 0 || lifecycle.storeFault) && (
                <Panel
                  flush
                  title="Action evidence that could not be written"
                  description={`${count(lifecycle.stuck)} still retrying, ${count(lifecycle.quarantined)} set aside after the same failure repeated (the ledger records each one, and they are still retried every six hours).${lifecycle.storeFault ? ' The record of these failures could not be read, so the counts may be out of date.' : ''}`}
                  actions={<StatusChip tone="warn" icon={ShieldAlert}>{count(lifecycle.stuck + lifecycle.quarantined)} faults</StatusChip>}
                >
                  {lifecycle.faults.length > 0
                    ? <Table stacked label="Lifecycle faults" columns={faultColumns} rows={lifecycle.faults} rowKey={(f) => `${f.actionId}:${f.eventId}`} stickyHeader={false} className="ui-table--tall" />
                    : null}
                </Panel>
              )}
              <Panel
                flush
                title={<>Audit ledger ({auditRows.length})</>}
                description="The last 200 rows of the tamper-evident chain"
                actions={(
                  <>
                    {capacity && capacityView && (
                      <StatusChip tone={capacityView.tone} icon={capacityView.alert ? ShieldAlert : undefined} title={`${count(capacity.receiptCount)} of ${count(capacity.receiptCap)} receipts`}>{capacityView.label}</StatusChip>
                    )}
                    {chain && (chain.ok ? <StatusChip tone={chainTone} icon={ShieldCheck}>chain intact</StatusChip> : <StatusChip tone={chainTone} icon={ShieldAlert}>broken @ {chain.brokenAtSeq}</StatusChip>)}
                    <LinkButton
                      size="sm"
                      icon={Download}
                      href={auditExportHref()}
                      download
                      title="Download every retained audit row as NDJSON, ending in a chain-verification line (docs/RUNBOOK-audit-export.md). The export is itself audited."
                    >
                      Export
                    </LinkButton>
                  </>
                )}
                footer={capacity && capacityView ? (
                  <div className="ui-foot-text">
                    <div className="ui-actions">
                      <span>Records written <strong>{count(capacity.recordCount)}</strong> <span className="ui-quiet">({count(capacity.retainedRecords)} of {count(capacity.recordCap)} kept on screen, oldest roll off)</span></span>
                      <span>Receipts <strong>{count(capacity.receiptCount)}</strong> of {count(capacity.receiptCap)} <span className="ui-quiet">({count(capacity.receiptsRemaining)} left)</span></span>
                      <span>
                        Waiting to be written <strong>{capacity.queue.depth}</strong>
                        {capacity.queue.dropped > 0 && <span className="ui-text-bad"> · {capacity.queue.dropped} dropped since restart</span>}
                        {(capacity.queue.failed ?? 0) > 0 && <span className="ui-text-bad" title={capacity.queue.lastFailure ? `last error: ${capacity.queue.lastFailure}` : undefined}> · {capacity.queue.failed} not written since restart</span>}
                      </span>
                    </div>
                    <Meter value={capacityView.percent / 100} label="Audit receipt capacity used" tone={capacityView.tone === 'ok' ? 'ok' : capacityView.tone} />
                  </div>
                ) : undefined}
              >
                <Table stacked
                  label="Audit ledger"
                  columns={auditColumns}
                  rows={auditRows}
                  rowKey={(a) => String(a.seq)}
                  loading={loading}
                  skeletonRows={6}
                  empty={<EmptyState compact icon={ScrollText} title="No audit events yet">Every action and admin change is recorded here as it happens.</EmptyState>}
                />
              </Panel>
            </>
          )}

          {tab === 'approvals' && (
            <>
              {petsAvailable && <PetLibraryReviews canModerate={canModeratePets} />}
              {approvalError && <Callout tone="bad" role="alert">{approvalError}</Callout>}
              <Panel
                flush
                title={<>Pending actions ({approvals.length})</>}
                description="Items marked awaiting approval are high-risk and need a different approver than the requester (separation of duties)"
              >
                <Table stacked
                  label="Pending actions"
                  columns={approvalColumns}
                  rows={approvals}
                  rowKey={(a) => a.id}
                  loading={loading}
                  skeletonRows={4}
                  selectedKey={expandedApproval}
                  detail={approvalDetail}
                  className="ui-table--tall"
                  empty={<EmptyState compact icon={Inbox} title="Nothing waiting">No actions awaiting confirmation or approval.</EmptyState>}
                />
              </Panel>
              {legacyQuarantine.length > 0 && (
                <Panel
                  flush
                  title="Legacy unsealed actions"
                  description="Quarantined during the governed-store upgrade and never executed"
                  actions={<StatusChip tone="warn" icon={ShieldAlert}>{count(legacyQuarantine.length)} quarantined</StatusChip>}
                >
                  <Table stacked label="Quarantined legacy actions" columns={quarantineColumns} rows={legacyQuarantine} rowKey={(e) => `${e.actionId}:${e.recordSha256}`} stickyHeader={false} />
                </Panel>
              )}
            </>
          )}
        </Stack>
      </div>
    </>
  );
}
